import clsx from 'clsx';
import {
  LandChangeValue,
  STAGE_4,
} from 'constants/FarmboxConstants/stage4.constants';
import { FieldType } from 'enums/FieldTypeEnums';
import { Stage4MetaData } from 'models/Farmbox/field.model';
import React from 'react';
import {
  UseFormResetField,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { getPastYearsForDropdown } from 'shared/utils/getPastYearsForDropdown.util';
import { CoProductsComponentProps } from 'views/ActivityDataEntry/FarmCommodity/CoProductsComponent';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import styles from './farmFieldFlow.module.scss';

interface LandChangeFieldProps extends CoProductsComponentProps {
  type: LandChangeValue;
  landUseMetaData?: Stage4MetaData[];
  landManagementMetaData?: Stage4MetaData[];
  harvestYear?: number;
  landManagementChangeLength?: number;
  landUseChangeLength?: number;
  fetchData: (params?: { 'input-field': string }) => void;
  isDisabled?: boolean;
  resetField: UseFormResetField<FieldValues>;
}

interface getCropResidueInitialFieldProps {
  fetchData: (params?: { 'input-field': string }) => void;
  isDisabled?: boolean;
}

const { LAND_CHANGES_LABELS, FIELD_NAMES, HEADING, PLACEHOLDER } = STAGE_4;

const { SELECT_USE, SELECT_MANAGEMENT, YEAR_CHANGED, LAND_CHANGED, COMMENTS } =
  LAND_CHANGES_LABELS;

const {
  LAND_LABEL_FIELD,
  LAND_YEAR_CHANGED_FIELD,
  LAND_CHANGED_PERCENTAGE_FIELD,
  COMMENTS_FIELD,
  LAND_MANAGEMENT_FIELD,
  LAND_USE_FIELD,
} = FIELD_NAMES;

const {
  COMMODITY,
  COMMODITY_LAND_USE_TRIGGER,
  COMMODITY_LAND_MANAGEMENT_TRIGGER,
} = HEADING;

const {
  SELECT_LAND_MANAGEMENT_CHANGE,
  SELECT_LAND_USE_CHANGE,
  ENTER_LAND_CHANGED,
  ENTER_COMMENTS,
  ENTER_YEAR_CHANGED,
} = PLACEHOLDER;

const { INPUT, SELECT } = FieldType;
const createOptions = (enumObject: object) =>
  Object.values(enumObject).map(value => ({ label: value, value }));

const transformToDropdownOptions = (data?: Stage4MetaData[]) => {
  return (
    data?.map(item => ({
      label: item.label,
      value: item.label,
    })) || []
  );
};

const getCommodityFieldName = (field: string) => `${COMMODITY}.${field}`;

export const GetLandChangeFields = (props: LandChangeFieldProps) => {
  const {
    remove,
    index,
    landManagementMetaData,
    landUseMetaData,
    type,
    harvestYear,
    landManagementChangeLength,
    landUseChangeLength,
    fetchData,
    isDisabled,
    resetField,
  } = props;
  const { watch, trigger } = useFormContext();
  const resetFields = (fields: string[]) => {
    fields.forEach(field => {
      resetField(getFieldPath(0, field), {
        defaultValue: null,
      });
    });
  };
  const removeHandler = () => {
    const fieldsToReset = [
      LAND_LABEL_FIELD,
      LAND_YEAR_CHANGED_FIELD,
      LAND_CHANGED_PERCENTAGE_FIELD,
    ];
    if (type === LandChangeValue.USED && landUseChangeLength === 1) {
      resetFields(fieldsToReset);
    } else if (
      type !== LandChangeValue.USED &&
      landManagementChangeLength === 1
    ) {
      resetFields(fieldsToReset);
    } else {
      const isUsed = type === LandChangeValue.USED;
      const fieldArray = isUsed
        ? COMMODITY_LAND_USE_TRIGGER
        : COMMODITY_LAND_MANAGEMENT_TRIGGER;
      remove?.(index);
      trigger(fieldArray);
    }
  };
  const landUseChange = watch(COMMODITY_LAND_USE_TRIGGER);
  const landManagementChange = watch(COMMODITY_LAND_MANAGEMENT_TRIGGER);

  const landOptions = transformToDropdownOptions(
    type === LandChangeValue.USED ? landUseMetaData : landManagementMetaData
  );
  const yearChangedOptions =
    harvestYear && getPastYearsForDropdown(harvestYear);

  const fieldPath =
    type === LandChangeValue.USED ? LAND_USE_FIELD : LAND_MANAGEMENT_FIELD;

  const getFieldPath = (index: number, field: string) =>
    `${COMMODITY}.${fieldPath}.${index}.${field}`;

  const selectLabel =
    type === LandChangeValue.USED ? SELECT_USE : SELECT_MANAGEMENT;
  const selectPlaceholder =
    type === LandChangeValue.USED
      ? SELECT_LAND_USE_CHANGE
      : SELECT_LAND_MANAGEMENT_CHANGE;

  const handleLandChanges = () => {
    const isUsed = type === LandChangeValue.USED;
    const fieldArray = isUsed ? landUseChange : landManagementChange;
    const fieldKey = isUsed ? LAND_USE_FIELD : LAND_MANAGEMENT_FIELD;
    const fieldNamesToTrigger = fieldArray.map((_: unknown, idx: number) => {
      return `commodity.${fieldKey}.${idx}`;
    });
    trigger(fieldNamesToTrigger);
  };

  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: getFieldPath(index, LAND_LABEL_FIELD),
          label: selectLabel,
          placeholderstring: selectPlaceholder,
          options: landOptions,
          onFocus: () =>
            fetchData({
              'input-field':
                selectLabel === SELECT_USE
                  ? 'farmbox_ag_commodity_select_land_use_change'
                  : 'farmbox_ag_commodity_select_land_management_change',
            }),
          disabled: isDisabled,
          onChange: () => handleLandChanges(),
        },
        span: 5,
      },
      {
        type: SELECT,
        fieldProps: {
          name: getFieldPath(index, LAND_YEAR_CHANGED_FIELD),
          label: YEAR_CHANGED,
          placeholderstring: ENTER_YEAR_CHANGED,
          options: yearChangedOptions,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_year_changed',
            }),
          disabled: isDisabled,
          onChange: () => handleLandChanges(),
        },
        span: 5,
      },
      {
        type: INPUT,
        fieldProps: {
          name: getFieldPath(index, LAND_CHANGED_PERCENTAGE_FIELD),
          label: LAND_CHANGED,
          placeholderstring: ENTER_LAND_CHANGED,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_land_changed_percentage',
            }),
          disabled: isDisabled,
          onChange: () => handleLandChanges(),
        },
        span: 5,
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: (
          <div
            className={clsx(styles.deleteContainer, {
              [styles.hideAction]: isDisabled,
            })}
            onClick={removeHandler}>
            <DeleteButton />
          </div>
        ),
        span: 2,
      },
    ],
  };
};

export const getCommentField = (props: getCropResidueInitialFieldProps) => {
  const { fetchData, isDisabled } = props;
  return {
    data: [
      {
        type: INPUT,
        fieldProps: {
          textarea: true,
          name: getCommodityFieldName(COMMENTS_FIELD),
          label: COMMENTS,
          placeholderstring: ENTER_COMMENTS,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_comments',
            }),
          disabled: isDisabled,
        },
        span: 15,
      },
    ],
  };
};